import { useState } from "react";
import {
  Camera,
  Mic,
  Monitor,
  Laptop,
  Smartphone,
  Tablet,
  Speaker,
  Headphones,
  Radio,
  Tv,
  Projector,
  Lightbulb,
  Lamp,
  Cable,
  Plug,
  Battery,
  HardDrive,
  Cpu,
  Server,
  Router,
  Wifi,
  Usb,
  Disc,
  Film,
  Video,
  Clapperboard,
  Music,
  Volume2,
  Aperture,
  Focus,
  ScanLine,
  Gauge,
  Wrench,
  Box,
  Package,
  Boxes,
  Container,
  Truck,
  type LucideIcon,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";

const ICONS: { name: string; icon: LucideIcon }[] = [
  { name: "camera", icon: Camera },
  { name: "mic", icon: Mic },
  { name: "monitor", icon: Monitor },
  { name: "laptop", icon: Laptop },
  { name: "smartphone", icon: Smartphone },
  { name: "tablet", icon: Tablet },
  { name: "speaker", icon: Speaker },
  { name: "headphones", icon: Headphones },
  { name: "radio", icon: Radio },
  { name: "tv", icon: Tv },
  { name: "projector", icon: Projector },
  { name: "lightbulb", icon: Lightbulb },
  { name: "lamp", icon: Lamp },
  { name: "cable", icon: Cable },
  { name: "plug", icon: Plug },
  { name: "battery", icon: Battery },
  { name: "hard-drive", icon: HardDrive },
  { name: "cpu", icon: Cpu },
  { name: "server", icon: Server },
  { name: "router", icon: Router },
  { name: "wifi", icon: Wifi },
  { name: "usb", icon: Usb },
  { name: "disc", icon: Disc },
  { name: "film", icon: Film },
  { name: "video", icon: Video },
  { name: "clapperboard", icon: Clapperboard },
  { name: "music", icon: Music },
  { name: "volume-2", icon: Volume2 },
  { name: "aperture", icon: Aperture },
  { name: "focus", icon: Focus },
  { name: "scan-line", icon: ScanLine },
  { name: "gauge", icon: Gauge },
  { name: "wrench", icon: Wrench },
  { name: "box", icon: Box },
  { name: "package", icon: Package },
  { name: "boxes", icon: Boxes },
  { name: "container", icon: Container },
  { name: "truck", icon: Truck },
];

const ICON_MAP = new Map(ICONS.map((i) => [i.name, i.icon]));

interface IconPickerProps {
  value?: string;
  onChange: (value: string) => void;
}

export const IconPicker = ({ value, onChange }: IconPickerProps) => {
  const [open, setOpen] = useState(false);

  const SelectedIcon = value ? ICON_MAP.get(value) : null;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          className="w-full justify-start gap-2 h-10"
          type="button"
        >
          {SelectedIcon ? (
            <>
              <SelectedIcon className="h-4 w-4" />
              <span>{value}</span>
            </>
          ) : (
            <span className="text-muted-foreground">Select an icon...</span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-2" align="start">
        <div className="grid grid-cols-6 gap-1">
          {ICONS.map(({ name, icon: Icon }) => (
            <Button
              key={name}
              variant="ghost"
              size="icon"
              type="button"
              className={cn(
                "h-10 w-10",
                value === name && "bg-accent text-accent-foreground",
              )}
              onClick={() => {
                onChange(name);
                setOpen(false);
              }}
              title={name}
            >
              <Icon className="h-5 w-5" />
            </Button>
          ))}
        </div>
        {value && (
          <Button
            variant="ghost"
            size="sm"
            className="w-full mt-2 text-muted-foreground"
            type="button"
            onClick={() => {
              onChange("");
              setOpen(false);
            }}
          >
            Clear selection
          </Button>
        )}
      </PopoverContent>
    </Popover>
  );
};

export const getEquipmentIcon = (
  iconName?: string | null,
): LucideIcon | null => {
  if (!iconName) return null;
  return ICON_MAP.get(iconName) || null;
};
